import * as React from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { Logo } from "@/components/logo";
import logoLight from "@/assets/capitaldart-logo-light.svg.asset.json";
import { MobileNav } from "@/components/marketing/mobile-nav";
import { useAuth } from "@/hooks/use-auth";

const LOGO_LIGHT = logoLight.url;

const NAVY = "#0B2545";
const NAVY_DEEP = "#07182E";
const PAPER = "#F4F1E8";
const PAPER_2 = "#FBFAF5";
const GOLD = "#C9A24B";
const GOLD_2 = "#D8B260";
const INK = "#14213A";
const INK_2 = "#545C6B";
const INK_MUTED = "#6B7384";
const LINE = "#E4DECF";
const NAVY_TEXT = "#EDEAE0";
const NAVY_TEXT_DIM = "#B6BFCD";
const NAVY_TEXT_DIM_2 = "#9AA5B4";

const FONT_SERIF = "'Spectral', Georgia, serif";
const FONT_SANS = "'IBM Plex Sans', system-ui, sans-serif";
const FONT_MONO = "'IBM Plex Mono', ui-monospace, monospace";

const TITLE = "Use CapitalDart in Claude or ChatGPT | Assistant setup";
const DESCRIPTION =
  "Connect CapitalDart to Claude or ChatGPT. Describe your raise, get ranked investor and acquirer firms and verified contacts from CapitalDart's directory, and draft outreach you send from your own accounts.";

export const Route = createFileRoute("/connect")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://capitaldart.com/connect" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: TITLE },
      { name: "twitter:description", content: DESCRIPTION },
    ],
    links: [{ rel: "canonical", href: "https://capitaldart.com/connect" }],
  }),
  component: ConnectHubPage,
});

function LandingLogo({ height = 32 }: { height?: number }) {
  return (
    <Logo
      src={LOGO_LIGHT}
      alt="CapitalDart"
      height={height}
      style={{ display: "block" }}
      trademarkClassName="text-[#F4F1E8]"
    />
  );
}

function GoldButton({
  children,
  to,
  search,
  style,
}: {
  children: React.ReactNode;
  to?: "/auth" | "/dashboard";
  search?: { mode: "signin" | "signup" };
  style?: React.CSSProperties;
}) {
  const base: React.CSSProperties = {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    fontFamily: FONT_SANS,
    fontWeight: 600,
    color: NAVY,
    background: GOLD,
    borderRadius: 2,
    letterSpacing: ".01em",
    textDecoration: "none",
    transition: "background .15s",
    ...style,
  };
  const onEnter = (e: React.MouseEvent<HTMLElement>) => {
    (e.currentTarget as HTMLElement).style.background = GOLD_2;
  };
  const onLeave = (e: React.MouseEvent<HTMLElement>) => {
    (e.currentTarget as HTMLElement).style.background = GOLD;
  };
  if (to && search) {
    return (
      <Link to={to} search={search} style={base} onMouseEnter={onEnter} onMouseLeave={onLeave}>
        {children}
      </Link>
    );
  }
  return (
    <Link to={to ?? "/dashboard"} style={base} onMouseEnter={onEnter} onMouseLeave={onLeave}>
      {children}
    </Link>
  );
}

function Kicker({
  children,
  color = INK_MUTED,
  style,
}: {
  children: React.ReactNode;
  color?: string;
  style?: React.CSSProperties;
}) {
  return (
    <div
      style={{
        fontFamily: FONT_MONO,
        fontSize: 12,
        letterSpacing: ".2em",
        textTransform: "uppercase",
        color,
        ...style,
      }}
    >
      {children}
    </div>
  );
}

function Hero() {
  const { session, loading } = useAuth();
  const isAuthed = Boolean(session);
  return (
    <section
      style={{
        background: NAVY,
        backgroundImage: `radial-gradient(120% 80% at 85% -10%, rgba(201,162,75,.12), rgba(11,37,69,0) 60%)`,
        color: NAVY_TEXT,
        position: "relative",
      }}
    >
      <div
        aria-hidden
        style={{
          position: "absolute",
          inset: 0,
          backgroundImage:
            "linear-gradient(rgba(255,255,255,.025) 1px,transparent 1px),linear-gradient(90deg,rgba(255,255,255,.025) 1px,transparent 1px)",
          backgroundSize: "64px 64px",
          pointerEvents: "none",
        }}
      />
      <nav
        className="mx-auto"
        style={{
          maxWidth: 1200,
          position: "relative",
          padding: "26px 32px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 24,
        }}
      >
        <Link to="/" hash="top" style={{ textDecoration: "none" }}>
          <LandingLogo height={34} />
        </Link>
        <div className="hidden lg:flex" style={{ alignItems: "center", gap: 18 }}>
          {loading ? null : isAuthed ? (
            <GoldButton to="/dashboard" style={{ fontSize: 14.5, padding: "11px 20px" }}>
              Dashboard
            </GoldButton>
          ) : (
            <>
              <Link
                to="/auth"
                search={{ mode: "signin" }}
                style={{
                  color: "#AEB8C7",
                  fontSize: 14.5,
                  fontFamily: FONT_SANS,
                  textDecoration: "none",
                }}
              >
                Sign in
              </Link>
              <GoldButton
                to="/auth"
                search={{ mode: "signup" }}
                style={{ fontSize: 14.5, padding: "11px 20px" }}
              >
                Get started
              </GoldButton>
            </>
          )}
        </div>
        <div className="lg:hidden">
          <MobileNav className="cd-connect-nav-mobile" />
        </div>
      </nav>

      <div
        className="mx-auto px-5 sm:px-8 lg:px-10"
        style={{ maxWidth: 1200, position: "relative", padding: "80px 0 96px" }}
      >
        <div className="max-w-3xl">
          <Kicker color={GOLD} style={{ marginBottom: 22 }}>
            CapitalDart for AI assistants
          </Kicker>
          <h1
            style={{
              fontFamily: FONT_SERIF,
              fontWeight: 500,
              fontSize: "clamp(40px,5vw,62px)",
              lineHeight: 1.04,
              letterSpacing: "-.015em",
              margin: "0 0 20px",
              color: "#F7F5EE",
            }}
          >
            Use CapitalDart inside Claude or ChatGPT.
          </h1>
          <p
            style={{
              fontSize: 18,
              lineHeight: 1.6,
              color: NAVY_TEXT_DIM,
              maxWidth: 700,
              margin: 0,
            }}
          >
            Connect CapitalDart once to the assistant you already use, then ask it to describe
            your deal, find investor and acquirer firms, get verified contacts, and draft outreach. The same
            account and credit balance apply in both assistants, and your deals, delivery
            tracking, and Investor Readiness Reports remain at capitaldart.com.
          </p>
        </div>
      </div>
    </section>
  );
}

const PATHS: Array<{ name: string; to: "/connect/claude" | "/connect/chatgpt"; body: string; cta: string }> = [
  {
    name: "Claude",
    to: "/connect/claude",
    body: "Add CapitalDart as a custom connector in Claude Settings, sign in, and approve access. Setup takes about two minutes and works in the desktop, web, and mobile apps.",
    cta: "Set up Claude",
  },
  {
    name: "ChatGPT",
    to: "/connect/chatgpt",
    body: "Add CapitalDart as a connector in ChatGPT settings on plans that support custom connectors, sign in, and approve access. Enable it in the conversation before you ask.",
    cta: "Set up ChatGPT",
  },
];

function Paths() {
  return (
    <section
      className="px-5 sm:px-8 lg:px-10"
      style={{ background: PAPER, padding: "88px 0", borderBottom: `1px solid ${LINE}` }}
    >
      <div className="mx-auto" style={{ maxWidth: 1100 }}>
        <Kicker style={{ marginBottom: 18 }}>Two paths</Kicker>
        <h2
          style={{
            fontFamily: FONT_SERIF,
            fontWeight: 500,
            fontSize: "clamp(30px,3.4vw,42px)",
            lineHeight: 1.08,
            letterSpacing: "-.01em",
            margin: "0 0 40px",
            maxWidth: 780,
          }}
        >
          Choose your assistant.
        </h2>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
            gap: 20,
          }}
        >
          {PATHS.map((p) => (
            <div
              key={p.name}
              style={{
                background: PAPER_2,
                border: `1px solid ${LINE}`,
                borderRadius: 5,
                padding: "30px 28px",
                display: "flex",
                flexDirection: "column",
                gap: 14,
              }}
            >
              <h3
                style={{
                  fontFamily: FONT_SERIF,
                  fontWeight: 600,
                  fontSize: 24,
                  lineHeight: 1.25,
                  margin: 0,
                }}
              >
                {p.name}
              </h3>
              <p style={{ fontSize: 15, lineHeight: 1.65, color: INK_2, margin: 0, flex: 1 }}>
                {p.body}
              </p>
              <Link
                to={p.to}
                style={{
                  fontFamily: FONT_SANS,
                  fontSize: 15,
                  fontWeight: 600,
                  color: INK,
                  textDecoration: `underline ${GOLD}`,
                  textUnderlineOffset: 4,
                }}
              >
                {p.cta}
              </Link>
            </div>
          ))}
        </div>
        <p style={{ fontSize: 15, lineHeight: 1.7, color: INK_2, margin: "36px 0 0" }}>
          Both paths use the same connector at capitaldart.com/mcp and the same tools. For what
          the connector can and cannot do in a conversation, read the{" "}
          <Link
            to="/connect/capabilities"
            style={{ color: INK, textDecoration: `underline ${GOLD}`, textUnderlineOffset: 4 }}
          >
            capabilities reference
          </Link>
          .
        </p>
      </div>
    </section>
  );
}

function CreditsAccount() {
  return (
    <section
      className="px-5 sm:px-8 lg:px-10"
      style={{ background: PAPER_2, padding: "88px 0", borderBottom: `1px solid ${LINE}` }}
    >
      <div className="mx-auto" style={{ maxWidth: 900 }}>
        <Kicker style={{ marginBottom: 18 }}>Credits and account</Kicker>
        <p
          style={{
            fontFamily: FONT_SERIF,
            fontSize: "clamp(20px,2.2vw,26px)",
            lineHeight: 1.4,
            margin: 0,
            color: INK,
          }}
        >
          Your assistant connection uses the same account and credit balance as capitaldart.com.
          Buy credits at{" "}
          <a
            href="https://capitaldart.com/buy-credits"
            style={{ color: INK, textDecoration: `underline ${GOLD}` }}
          >
            capitaldart.com/buy-credits
          </a>{" "}
          and they are available in the assistant immediately, with no reconnect needed.
        </p>
      </div>
    </section>
  );
}

function FinalCta() {
  return (
    <section
      style={{ background: NAVY, color: NAVY_TEXT, padding: "96px 0", position: "relative" }}
    >
      <div
        aria-hidden
        style={{
          position: "absolute",
          inset: 0,
          backgroundImage: `radial-gradient(90% 70% at 50% 120%, rgba(201,162,75,.14), rgba(11,37,69,0) 60%)`,
          pointerEvents: "none",
        }}
      />
      <div
        className="mx-auto px-5 sm:px-8 lg:px-10"
        style={{ position: "relative", maxWidth: 900, textAlign: "center" }}
      >
        <Kicker color={GOLD} style={{ marginBottom: 22 }}>
          The full workflow
        </Kicker>
        <h2
          style={{
            fontFamily: FONT_SERIF,
            fontWeight: 500,
            fontSize: "clamp(32px,4.2vw,52px)",
            lineHeight: 1.06,
            letterSpacing: "-.015em",
            margin: "0 0 22px",
            color: "#F7F5EE",
          }}
        >
          Ready for the full workflow?
        </h2>
        <p
          style={{
            fontSize: 17,
            lineHeight: 1.6,
            color: NAVY_TEXT_DIM,
            maxWidth: 640,
            margin: "0 auto 40px",
          }}
        >
          Upload your deck at capitaldart.com for matched investors and acquirers, outreach drafts, an
          Investor Readiness Report, and a pipeline to track it all.
        </p>
        <GoldButton
          to="/auth"
          search={{ mode: "signup" }}
          style={{ fontSize: 16, padding: "16px 34px" }}
        >
          Get started at capitaldart.com
        </GoldButton>
      </div>
    </section>
  );
}

function LandingFooter() {
  return (
    <footer style={{ background: NAVY_DEEP, color: NAVY_TEXT_DIM_2, padding: "48px 0" }}>
      <div
        className="mx-auto px-5 sm:px-8 lg:px-10"
        style={{
          maxWidth: 1200,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 24,
          flexWrap: "wrap",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
          <LandingLogo height={26} />
          <span style={{ fontSize: 13.5, color: "#56627A", marginLeft: 14, fontFamily: FONT_SANS }}>
            © 2026
          </span>
        </div>
        <div style={{ display: "flex", gap: 26, fontSize: 14, fontFamily: FONT_SANS, flexWrap: "wrap" }}>
          <Link to="/help" style={{ color: "inherit", textDecoration: "none" }}>
            Help
          </Link>
          <Link to="/privacy" style={{ color: "inherit", textDecoration: "none" }}>
            Privacy
          </Link>
          <Link to="/terms" style={{ color: "inherit", textDecoration: "none" }}>
            Terms
          </Link>
          <Link to="/connect" style={{ color: "inherit", textDecoration: "none" }}>
            Use with Claude / ChatGPT
          </Link>
          <Link to="/connect/capabilities" style={{ color: "inherit", textDecoration: "none" }}>
            Claude / ChatGPT capabilities
          </Link>
          <Link to="/partners" style={{ color: "inherit", textDecoration: "none" }}>
            Partners
          </Link>
          <Link to="/lists" style={{ color: "inherit", textDecoration: "none" }}>
            Top 10 Investor Rankings
          </Link>
          <Link to="/contact" style={{ color: "inherit", textDecoration: "none" }}>
            Contact
          </Link>
        </div>
      </div>
    </footer>
  );
}

function ConnectHubPage() {
  return (
    <main
      id="top"
      style={{ background: PAPER, color: INK, fontFamily: FONT_SANS }}
      className="min-h-screen antialiased overflow-x-clip"
    >
      <style>{`
        @media (max-width: 1023.98px) { .cd-connect-nav-mobile { display: inline-flex !important; } }
      `}</style>
      <Hero />
      <Paths />
      <CreditsAccount />
      <FinalCta />
      <LandingFooter />
    </main>
  );
}
